import { Loader2Icon } from "lucide-react";
import { Switch } from "@ibada/ui";

import { useTranslations } from "@/i18n";
import { usePushSubscription } from "@/features/push/hooks/use-push-subscription";

/**
 * "Enable notifications on this device" control, rendered inside the Clerk UserProfile
 * Notifications page (next to theme preferences). A single switch for now — no category
 * toggles. Permission is only requested on toggle-on; a denied permission shows guidance
 * and does not re-prompt.
 */
export function DevicePushPreferencePanel({ embedded = false }: { embedded?: boolean } = {}) {
  const t = useTranslations("settings.devicePush");
  const { status, isBusy, errorReason, enable, disable } = usePushSubscription();

  const isEnabled = status === "enabled";
  const isUnsupported = status === "unsupported";
  const isBlocked = status === "blocked";
  const isLoading = status === "loading";
  const switchDisabled = isBusy || isLoading || isUnsupported || isBlocked;

  const handleChange = (checked: boolean) => {
    if (checked) {
      void enable();
    } else {
      void disable();
    }
  };

  const errorText =
    errorReason === "not-configured"
      ? t("notConfigured")
      : errorReason === "no-service-worker"
        ? t("noServiceWorker")
        : errorReason === "generic"
          ? t("error")
          : null;

  return (
    <div className='space-y-5'>
      {embedded ? null : (
        <>
          <h1 className='text-xl font-semibold tracking-tight text-foreground'>{t("title")}</h1>
          <div className='border-t border-border' />
        </>
      )}

      <div className='flex items-start justify-between gap-4'>
        <div className='space-y-1'>
          <span className='text-sm font-medium text-foreground'>{t("deviceToggleLabel")}</span>
          <p className='text-xs text-muted-foreground'>{t("deviceToggleDescription")}</p>
        </div>

        <div className='flex items-center gap-2'>
          {isBusy || isLoading ? (
            <Loader2Icon className='size-4 animate-spin text-muted-foreground' aria-hidden />
          ) : null}
          <Switch
            id='device-push-toggle'
            aria-label={t("deviceToggleLabel")}
            aria-busy={isBusy}
            checked={isEnabled}
            disabled={switchDisabled}
            onCheckedChange={handleChange}
          />
        </div>
      </div>

      {isEnabled ? <p className='text-xs text-muted-foreground'>{t("enabled")}</p> : null}
      {isUnsupported ? <p className='text-xs text-muted-foreground'>{t("unsupported")}</p> : null}
      {isBlocked ? <p className='text-xs text-amber-600 dark:text-amber-500'>{t("blocked")}</p> : null}
      {errorText ? <p className='text-xs text-destructive'>{errorText}</p> : null}
    </div>
  );
}
